import React from 'react';
import { motion } from 'framer-motion';

const WhatIUse = () => {
  const categories = [
    {
      title: "Workstation",
      tools: [
        {
          title: "Dell Inspiron 5518",
          description: "My reliable development machine that handles everything from Web2 to Web3 development. Perfect balance of performance and portability for both university work and professional development."
        }
      ]
    },
    {
      title: "Development tools",
      tools: [
        {
          title: "Visual Studio Code",
          description: "My primary IDE for web development. The extensive ecosystem of extensions and excellent integration with WSL makes it perfect for both frontend and blockchain development."
        },
        {
          title: "Eclipse",
          description: "Go-to IDE for Java development, particularly useful for university projects and working with Spring framework."
        },
        {
          title: "Cursor",
          description: "An AI-powered code editor that helps boost productivity with smart code completions and refactoring suggestions."
        },
        {
          title: "Brave Browser",
          description: "Privacy-focused browser with built-in crypto wallet support, perfect for Web3 development and testing."
        },
        {
          title: "WSL (Windows Subsystem for Linux)",
          description: "Brings the power of Linux development environment to Windows, essential for running Docker containers and maintaining development parity."
        },
        {
          title: "Git & GitHub",
          description: "Version control essentials that power all my projects, from personal experiments to professional collaborations."
        }
      ]
    },
    {
      title: "Design",
      tools: [
        {
          title: "Figma",
          description: "The industry-standard design tool I use for UI/UX design and prototyping. Perfect for visualizing ideas before implementation."
        },
        {
          title: "Visily",
          description: "AI-powered design tool that helps speed up the wireframing and prototyping process, especially useful for quick mockups."
        }
      ]
    },
    {
      title: "Productivity",
      tools: [
        {
          title: "Lucidchart",
          description: "My go-to tool for creating flowcharts, system architecture diagrams, and visualizing complex workflows."
        },
        {
          title: "Notion",
          description: "All-in-one workspace for note-taking, project management, and documentation. Helps keep everything organized and accessible."
        }
      ]
    }
  ];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            What I{' '}
            <span className="bg-gradient-to-r from-accent to-warning bg-clip-text text-transparent">
              Use
            </span>
          </h2>
          <p className="font-body text-text-secondary text-lg max-w-3xl mx-auto">
            Take a peek into the tools and technologies that help me build everything from web apps to blockchain solutions. Here's what keeps my development engine running.
          </p>
        </motion.div>

        {/* Tools Grid */}
        <div className="space-y-16">
          {categories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h3 className="font-heading text-2xl font-bold text-primary mb-6 border-l-4 border-accent pl-4">
                {category.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.tools.map((tool, toolIndex) => (
                  <motion.div
                    key={tool.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: toolIndex * 0.1 + categoryIndex * 0.05 }}
                    viewport={{ once: true }}
                    className="bg-background border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-300"
                  >
                    <h4 className="font-heading font-semibold text-xl text-primary mb-3">{tool.title}</h4>
                    <p className="font-body text-text-secondary">{tool.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhatIUse; 